import {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
  type SetStateAction,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ImportRowListItem,
  ImportChangeWithTraveller,
} from './ImportRowListItem';
import { ImportRowDetailDialog } from './ImportRowDetailDialog';
import {
  flattenObject,
  importChangeKey,
  parseErrorMessage,
} from './importRowUtils';
import { VirtualizedList } from '@/components/VirtualizedList.tsx';
import { FilteredListContainer } from '@/components/FilteredListContainer';
import { RightPanel } from '@/components/RightPanel/RightPanel';
import { FilterPanel } from '@/components/FilterPanel';
import { Dropdown, OptionsType } from '@/components/core/Dropdown';
import Input from '@/components/core/Input';
import Button from '@/components/core/Button';
import { useImportFileStore } from '@/stores/importFile';
import { useSearchFilterSort } from '@/hooks/useSearchFilterSort';
import { useAscOrDescIcon } from '@/hooks/useSort';
import { FilterType } from '@/types/filterSearch';
import { createSortConfig } from '@/utils/sort';
import type { ImportSearchParams } from '@/components/Import/importSearchSchema';
import type { SearchFilter } from '@/types/filterSearch';
import { StudentType, type FormattedStudent } from '@/types/students';
import { useTenantID } from '@/hooks/useTenantID';
import { useHalfProductAvailability } from '@/hooks/useHalfProductAvailability';
import { useGetStudentCollection } from '@/hooks/useGetStudentCollection';
import { useValidateCollectionMemberImport } from '@/api/trafikportalen';
import {
  formatImportNin,
  sanitizeImportRowsForValidation,
} from '@/helpers/transformImportData';
import { normalizeCareOf } from '@/helpers/careOf';
import { toast } from '@/hooks/useToast';
import {
  getComparisonFields,
  getImportStateMeta,
  ImportState,
} from '@/helpers/import';
import { useImportStatusLabels } from '@/hooks/useImportStatusLabels';
import type { CollectionImportRowDTO } from '@/api/trafikportalen';

interface ImportChangeWithSearchFields extends ImportChangeWithTraveller {
  first_name: string;
  last_name: string;
  national_identity_number: string;
  email: string;
  row_number: number | undefined;
  displayAction: string;
}

interface ImportValidateProps {
  searchParams: ImportSearchParams & SearchFilter;
  type: StudentType;
}

interface OpenImportRowDialogProps {
  rowKey: string;
  studentType: StudentType;
  disableHalfProduct: boolean;
  isSaving: boolean;
  existingStudents?: FormattedStudent[];
  onOpenChange: (open: SetStateAction<boolean>) => void;
  onSave: (rowNumber: number, editedData: Record<string, string>) => void;
  onShowDuplicateNin: (nin: string) => void;
}

const OpenImportRowDialog = ({
  rowKey,
  studentType,
  disableHalfProduct,
  isSaving,
  existingStudents,
  onOpenChange,
  onSave,
  onShowDuplicateNin,
}: OpenImportRowDialogProps) => {
  const {
    importData,
    originalImportDataByRowNumber,
    failedChanges,
    successChanges,
  } = useImportFileStore();

  const change = useMemo(() => {
    return [...failedChanges, ...successChanges].find(
      (item) => importChangeKey(item) === rowKey,
    );
  }, [failedChanges, successChanges, rowKey]);

  if (!change) return null;

  const row = importData.find((item) => item.row_number === change.row_number);
  const rowDataObject = row ? flattenObject(row) : {};
  const originalFromFile =
    change.row_number != null
      ? originalImportDataByRowNumber[change.row_number]
      : undefined;

  return (
    <ImportRowDetailDialog
      open
      onOpenChange={onOpenChange}
      change={change}
      parsedErrors={
        change.error_message ? parseErrorMessage(change.error_message) : []
      }
      rowDataObject={rowDataObject}
      importedFileRowDataObject={
        originalFromFile != null
          ? flattenObject(originalFromFile)
          : rowDataObject
      }
      existingData={flattenObject(
        existingStudents?.find(
          (student) => student.id === change.collection_member_id,
        ),
      )}
      comparisonFields={getComparisonFields(studentType, disableHalfProduct)}
      onSave={onSave}
      isSaving={isSaving}
      studentType={studentType}
      disableHalfProduct={disableHalfProduct}
      onShowDuplicateNin={onShowDuplicateNin}
    />
  );
};

export const ImportValidate = ({ searchParams, type }: ImportValidateProps) => {
  const tenantId = useTenantID();
  const isHalfProductAvailable = useHalfProductAvailability();
  const disableHalfProduct = !isHalfProductAvailable(type);
  const { formatMessage } = useIntl();
  const rightPanelRef = useRef<HTMLElement>(null);
  const { query, sort, direction } = searchParams;
  const [isSaving, setIsSaving] = useState(false);
  const [openRowKey, setOpenRowKey] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const {
    validationResult,
    failedChanges,
    successChanges,
    importData,
    updateImportRow,
    markRowEdited,
    setValidationResult,
    editedRows,
  } = useImportFileStore();

  const { students: existingStudents, collectionId } =
    useGetStudentCollection(type);

  const validateImport = useValidateCollectionMemberImport();

  const allChanges = useMemo((): ImportChangeWithTraveller[] => {
    const changes = [...failedChanges, ...successChanges];
    return changes.map((change) => ({
      change,
      traveller: undefined,
    }));
  }, [failedChanges, successChanges]);

  const getSearchableFields = useCallback(
    (
      item: ImportChangeWithTraveller,
    ): Pick<
      ImportChangeWithSearchFields,
      'first_name' | 'last_name' | 'national_identity_number' | 'email'
    > => {
      const row = importData.find(
        (r) => r.row_number === item.change.row_number,
      );

      const existing = existingStudents?.find(
        (s) => s.id === item.change.collection_member_id,
      );

      const firstName =
        item.traveller?.traveller?.first_name ||
        row?.first_name ||
        existing?.first_name ||
        '';
      const lastName =
        item.traveller?.traveller?.last_name ||
        row?.last_name ||
        existing?.last_name ||
        '';

      return {
        first_name: firstName,
        last_name: lastName,
        national_identity_number:
          row?.national_identity_number ||
          existing?.national_identity_number ||
          '',
        email: row?.email || existing?.email || '',
      };
    },
    [importData, existingStudents],
  );

  const searchKeys = useMemo((): (keyof ImportChangeWithSearchFields)[] => {
    return ['first_name', 'last_name', 'national_identity_number', 'email'];
  }, []);

  const sortConfigs = useMemo(() => {
    const configs = createSortConfig<ImportChangeWithSearchFields>({
      textFields: ['first_name', 'last_name'],
    });

    configs.row_number = (a, b) => {
      if (a.row_number == null && b.row_number == null) return 0;
      if (a.row_number == null) return -1;
      if (b.row_number == null) return 1;
      return a.row_number - b.row_number;
    };

    return configs;
  }, []);

  const itemsWithSearchFields = useMemo((): ImportChangeWithSearchFields[] => {
    return allChanges.map((item) => {
      const existingStudent = existingStudents?.find(
        (s) => s.id === item.change.collection_member_id,
      );
      const isManuallyModifiedUpdate =
        item.change.action === 'updated' &&
        existingStudent?.manually_modified === true;

      return {
        ...item,
        ...getSearchableFields(item),
        row_number:
          item.change.action === 'removed' ? undefined : item.change.row_number,
        displayAction: isManuallyModifiedUpdate
          ? 'updatedManuallyModified'
          : item.change.action,
      };
    });
  }, [allChanges, getSearchableFields, existingStudents]);

  const {
    filterSearch,
    sortedItems,
    setFilterSearch,
    handleSort,
    handleSearchParams,
    handleSearch,
  } = useSearchFilterSort<ImportChangeWithSearchFields>({
    items: itemsWithSearchFields,
    searchKeys,
    sortConfigs,
    routePath: `/${tenantId}/students/${type}/import`,
    defaultSearchParams: {
      sort: 'row_number',
      direction: 'asc',
    },
  });

  const getIcon = useAscOrDescIcon();

  const statusLabels = useImportStatusLabels();

  const updatedManuallyModifiedCount = useMemo(() => {
    return itemsWithSearchFields.filter(
      (item) => item.displayAction === 'updatedManuallyModified',
    ).length;
  }, [itemsWithSearchFields]);

  const statusOptions = useMemo((): OptionsType[] => {
    const summary = validationResult?.summary;

    const statuses = [
      { id: 'failed', count: failedChanges.length },
      { id: 'added', count: summary?.added ?? 0 },
      {
        id: 'updated',
        count: (summary?.updated ?? 0) - updatedManuallyModifiedCount,
      },
      { id: 'updatedManuallyModified', count: updatedManuallyModifiedCount },
      { id: 'restored', count: summary?.restored ?? 0 },
      { id: 'removed', count: summary?.removed ?? 0 },
      { id: 'skipped', count: summary?.skipped ?? 0 },
      { id: 'confirmed', count: summary?.confirmed ?? 0 },
    ];

    return statuses.map(({ id, count }) => ({
      id,
      label: `${statusLabels[id]} (${count})`,
    }));
  }, [
    failedChanges.length,
    validationResult?.summary,
    statusLabels,
    updatedManuallyModifiedCount,
  ]);

  const filteredChanges = useMemo(() => {
    if (!sortedItems) {
      return [];
    }

    const selectedStatuses = filterSearch?.state;
    if (!selectedStatuses || selectedStatuses.length === 0) {
      return sortedItems;
    }

    const selectedIds = selectedStatuses.map((s) => s.id);

    return sortedItems.filter((item) => {
      return selectedIds.includes(item.displayAction);
    });
  }, [sortedItems, filterSearch?.state]);

  const handleFilter = useCallback(
    (filters: OptionsType[]) => {
      const filterTypes: FilterType[] = filters.map((f) => ({
        id: String(f.id),
        label: String(f.label ?? ''),
      }));
      setFilterSearch((prev) => ({
        ...prev!,
        state: filterTypes,
      }));
      handleSearchParams({
        state: filters.map((f) => String(f.id)),
      });
    },
    [handleSearchParams, setFilterSearch],
  );

  const activeStatuses = useMemo(
    () => (filterSearch?.state ?? []).map((s) => s.id),
    [filterSearch?.state],
  );

  const handleStatusClick = useCallback(
    (statusId: string) => {
      const isAlreadySoloActive =
        activeStatuses.length === 1 && activeStatuses[0] === statusId;

      if (isAlreadySoloActive) {
        handleFilter([]);
        return;
      }

      const option = statusOptions.find((o) => String(o.id) === statusId);
      if (!option) return;

      handleFilter([option]);
    },
    [activeStatuses, statusOptions, handleFilter],
  );

  const handleDetailOpenChange = useCallback(
    (nextOpen: SetStateAction<boolean>) => {
      setOpenRowKey((current) => {
        const isOpen = current != null;
        const resolved =
          typeof nextOpen === 'function' ? nextOpen(isOpen) : nextOpen;
        return resolved ? current : null;
      });
    },
    [],
  );

  const handleShowDuplicateNin = useCallback(
    (nin: string) => {
      setFilterSearch((prev) =>
        prev ? { ...prev, query: nin, state: [] } : prev,
      );
      handleSearchParams({ query: nin, state: [] });
    },
    [setFilterSearch, handleSearchParams],
  );

  const handleRowSave = useCallback(
    async (rowNumber: number, editedData: Record<string, string>) => {
      if (!collectionId) return;

      setIsSaving(true);
      try {
        const currentImportData = useImportFileStore.getState().importData;
        const originalRow = currentImportData.find(
          (r) => r.row_number === rowNumber,
        );
        const updatedRow: CollectionImportRowDTO = {
          ...(originalRow ?? { row_number: rowNumber, half_product: false }),
        };

        const stringFields = [
          'first_name',
          'last_name',
          'national_identity_number',
          'email',
          'phone',
          'guardian1_email',
          'guardian2_email',
          'school_name',
          'school_bus_stop',
          'guardian1_bus_stop',
          'guardian2_bus_stop',
        ] as const;

        for (const field of stringFields) {
          if (field in editedData) {
            if (editedData[field]) {
              updatedRow[field] =
                field === 'national_identity_number'
                  ? formatImportNin(editedData[field])
                  : editedData[field];
            } else {
              delete (updatedRow as unknown as Record<string, unknown>)[field];
            }
          }
        }

        if (!disableHalfProduct && 'half_product' in editedData) {
          const v = editedData.half_product.toLowerCase();
          updatedRow.half_product = v === 'true' || v === '1' || v === 'ja';
        }

        const addressKeys = [
          'delivery_address',
          'guardian1_address',
          'guardian2_address',
          'school_address',
        ] as const;

        for (const addrKey of addressKeys) {
          const street = editedData[`${addrKey}.street`];
          const zipCode = editedData[`${addrKey}.zip_code`];
          const city = editedData[`${addrKey}.city`];
          const careOf = editedData[`${addrKey}.care_of`];

          if (
            street !== undefined ||
            zipCode !== undefined ||
            city !== undefined ||
            careOf !== undefined
          ) {
            const existing = originalRow?.[addrKey];
            const careOfName = normalizeCareOf(careOf ?? existing?.care_of);
            updatedRow[addrKey] = {
              street: street ?? existing?.street ?? '',
              zip_code: zipCode ?? existing?.zip_code ?? '',
              city: city ?? existing?.city ?? '',
              ...(careOfName && { care_of: careOfName }),
            };
          }
        }

        const updatedImportData = currentImportData.map((row) =>
          row.row_number === rowNumber ? updatedRow : row,
        );

        const result = await validateImport.mutateAsync({
          collectionID: collectionId,
          data: {
            rows: sanitizeImportRowsForValidation(updatedImportData),
            strategy: 'replace',
          },
        });

        updateImportRow(rowNumber, updatedRow);
        setValidationResult(result);
        markRowEdited(rowNumber);
      } catch {
        toast.error({
          title: formatMessage({
            defaultMessage: 'Ett fel uppstod vid validering',
          }),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      collectionId,
      updateImportRow,
      markRowEdited,
      validateImport,
      setValidationResult,
      formatMessage,
      disableHalfProduct,
    ],
  );

  const renderChange = useCallback(
    (item: ImportChangeWithSearchFields) => {
      const existingStudent = existingStudents?.find(
        (s) => s.id === item.change.collection_member_id,
      );

      return (
        <ImportRowListItem
          key={importChangeKey(item.change)}
          item={item}
          fileRowNumber={item.row_number}
          existingData={flattenObject(existingStudent)}
          onOpen={() => setOpenRowKey(importChangeKey(item.change))}
          isEditedInValidation={
            item.change.row_number != null &&
            editedRows.has(item.change.row_number)
          }
          displayAction={item.displayAction}
          studentType={type}
          disableHalfProduct={disableHalfProduct}
        />
      );
    },
    [existingStudents, type, editedRows, disableHalfProduct],
  );

  const stateCounts = useMemo((): { state: ImportState; count: number }[] => {
    if (!validationResult) return [];

    const summary = validationResult.summary;
    const entries: { state: ImportState; count: number }[] = [
      { state: 'added', count: summary.added },
      {
        state: 'updated',
        count: summary.updated - updatedManuallyModifiedCount,
      },
      {
        state: 'updatedManuallyModified',
        count: updatedManuallyModifiedCount,
      },
      { state: 'restored', count: summary.restored },
      { state: 'removed', count: summary.removed },
      { state: 'skipped', count: summary.skipped },
      { state: 'failed', count: summary.failed },
      { state: 'confirmed', count: summary.confirmed },
    ];

    return entries.filter((e) => e.count > 0);
  }, [validationResult, updatedManuallyModifiedCount]);

  const stateButtons = useMemo(() => {
    if (!validationResult) {
      return [];
    }

    const buttons = [
      <Button key="all" variant="outline" onClick={() => handleFilter([])}>
        <FormattedMessage defaultMessage="Alla" />
      </Button>,
    ];

    stateCounts.forEach(({ state, count }) => {
      const { icon: Icon, textColor } = getImportStateMeta(state);
      buttons.push(
        <Button
          key={state}
          variant="outline"
          className={textColor}
          startIcon={<Icon />}
          onClick={() => handleStatusClick(state)}
        >
          {count}{' '}
          <FormattedMessage
            id={`import.state.${state}${count === 1 ? '' : 'Plural'}`}
          />
        </Button>,
      );
    });

    return buttons;
  }, [validationResult, handleFilter, handleStatusClick, stateCounts]);

  useEffect(() => {
    if (filterSearch || statusOptions.length === 0) return;

    setFilterSearch({
      query: query ?? '',
      state: [],
      sort: sort ?? 'row_number',
      direction: direction ?? 'asc',
    });
  }, [filterSearch, statusOptions, setFilterSearch, query, sort, direction]);

  useEffect(() => {
    if (!filterSearch || !filteredChanges) return;
    const count = filteredChanges.length;
    setAnnouncement(
      formatMessage(
        {
          defaultMessage: '{count, plural, one {# rad} other {# rader}} visas',
        },
        { count },
      ),
    );
  }, [filterSearch, filteredChanges, formatMessage]);

  if (!validationResult) {
    return (
      <div className="flex w-full items-center justify-center p-8">
        <span className="text-text-700">
          <FormattedMessage defaultMessage="Ingen valideringsdata tillgänglig" />
        </span>
      </div>
    );
  }

  return (
    <FilteredListContainer className="size-full">
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      <RightPanel ref={rightPanelRef}>
        {filterSearch && (
          <FilterPanel
            firstInput={
              <Input
                type="text"
                label={formatMessage({ defaultMessage: 'Sök' })}
                placeholder={formatMessage({
                  defaultMessage: 'Sök på namn, personnummer eller e-post',
                })}
                id="import-search"
                value={filterSearch.query ?? ''}
                onChange={(e) => handleSearch(e.target.value)}
              />
            }
            sortingOptions={[
              <Button
                key="sort-row-number"
                variant={
                  filterSearch.sort === 'row_number' ? 'filled' : 'outline'
                }
                startIcon={getIcon({
                  selectedSort: filterSearch.sort!,
                  wantedSort: 'row_number',
                  sortDirection: filterSearch.direction!,
                })}
                onClick={() => handleSort('row_number')}
                aria-pressed={filterSearch.sort === 'row_number'}
                aria-label={formatMessage(
                  {
                    defaultMessage: 'Sortera efter radnummer {direction}',
                  },
                  {
                    direction:
                      filterSearch.direction === 'asc'
                        ? formatMessage({ defaultMessage: 'stigande' })
                        : formatMessage({ defaultMessage: 'fallande' }),
                  },
                )}
              >
                <FormattedMessage defaultMessage="Radnummer" />
              </Button>,
              <Button
                key="sort-first-name"
                variant={
                  filterSearch.sort === 'first_name' ? 'filled' : 'outline'
                }
                startIcon={getIcon({
                  selectedSort: filterSearch.sort!,
                  wantedSort: 'first_name',
                  sortDirection: filterSearch.direction!,
                })}
                onClick={() => handleSort('first_name')}
                aria-pressed={filterSearch.sort === 'first_name'}
                aria-label={formatMessage(
                  {
                    defaultMessage: 'Sortera efter förnamn {direction}',
                  },
                  {
                    direction:
                      filterSearch.direction === 'asc'
                        ? formatMessage({ defaultMessage: 'stigande' })
                        : formatMessage({ defaultMessage: 'fallande' }),
                  },
                )}
              >
                <FormattedMessage defaultMessage="Förnamn" />
              </Button>,
              <Button
                key="sort-last-name"
                variant={
                  filterSearch.sort === 'last_name' ? 'filled' : 'outline'
                }
                startIcon={getIcon({
                  selectedSort: filterSearch.sort!,
                  wantedSort: 'last_name',
                  sortDirection: filterSearch.direction!,
                })}
                onClick={() => handleSort('last_name')}
                aria-pressed={filterSearch.sort === 'last_name'}
                aria-label={formatMessage(
                  {
                    defaultMessage: 'Sortera efter efternamn {direction}',
                  },
                  {
                    direction:
                      filterSearch.direction === 'asc'
                        ? formatMessage({ defaultMessage: 'stigande' })
                        : formatMessage({ defaultMessage: 'fallande' }),
                  },
                )}
              >
                <FormattedMessage defaultMessage="Efternamn" />
              </Button>,
            ]}
            filterOptions={[
              <Dropdown
                key="status-filter"
                label={formatMessage({ defaultMessage: 'Status' })}
                id="status-selection"
                options={statusOptions}
                placeholder={formatMessage({ defaultMessage: 'Alla' })}
                value={filterSearch.state ?? []}
                onValueChange={handleFilter}
                multiSelect
                containerRef={rightPanelRef}
              />,
            ]}
          />
        )}
      </RightPanel>
      <div className="flex size-full flex-col md:flex-[4]">
        <div className="flex flex-wrap gap-1 px-4 md:px-8">{stateButtons}</div>
        <VirtualizedList
          count={filteredChanges?.length ?? 0}
          countName={formatMessage(
            { defaultMessage: 'rader · {fileTotal} i filen' },
            { fileTotal: importData.length },
          )}
          totalItems={itemsWithSearchFields}
          filteredItems={filteredChanges ?? []}
          renderItem={renderChange}
          getItemKey={(item) => importChangeKey(item.change)}
        />
      </div>
      {openRowKey ? (
        <OpenImportRowDialog
          rowKey={openRowKey}
          studentType={type}
          disableHalfProduct={disableHalfProduct}
          isSaving={isSaving}
          existingStudents={existingStudents}
          onOpenChange={handleDetailOpenChange}
          onSave={handleRowSave}
          onShowDuplicateNin={handleShowDuplicateNin}
        />
      ) : null}
    </FilteredListContainer>
  );
};
